import { FC, KeyboardEvent } from "react";

import { Stack } from "@mui/material";
import { FormType, TicketingFormCategory } from "@simplyk/common";

import { AssistantPaymentCard } from "./helpers/parsePaymentCards";
import { usePaymentCardFormat } from "./helpers/usePaymentCardFormat";
import { PaymentCardActions } from "./PaymentCardActions/PaymentCardActions";

import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardTypography } from "@/dashboard-design-system/Typography";
import { DonorPaymentControl } from "@/features/DonorDashboard/DonorPaymentDetailDrawer/types";

export interface PaymentChoice {
  payment: AssistantPaymentCard;
  text: string;
}

interface PaymentCardsProps {
  payments: AssistantPaymentCard[];
  chosenPaymentId?: string | null;
  onChoose?: (choice: PaymentChoice) => void;
  onViewDetails?: (payment: AssistantPaymentCard, control?: DonorPaymentControl) => void;
  actions?: { control: DonorPaymentControl | null };
}

export const PaymentCards: FC<PaymentCardsProps> = ({
  payments,
  chosenPaymentId,
  onChoose,
  onViewDetails,
  actions,
}) => {
  const { t } = useTranslate();
  const formatPayment = usePaymentCardFormat();
  const describeItems = (payment: AssistantPaymentCard, date: string) => {
    const itemCount = payment.itemCount ?? 1;
    if (payment.formType === FormType.DonationForm) {
      return t("dashboard", "donorAssistant.paymentCard.donations", { count: itemCount, date });
    }
    if (payment.formCategory === TicketingFormCategory.Event) {
      return t("dashboard", "donorAssistant.paymentCard.tickets", { count: itemCount, date });
    }
    if (payment.formCategory === TicketingFormCategory.MembershipV2) {
      return t("dashboard", "donorAssistant.paymentCard.memberships", { count: itemCount, date });
    }
    return t("dashboard", "donorAssistant.paymentCard.items", { count: itemCount, date });
  };
  const visiblePayments = chosenPaymentId ? payments.filter((payment) => payment.id === chosenPaymentId) : payments;
  const actionsPayment = actions && !onChoose && visiblePayments.length === 1 ? visiblePayments[0] : null;
  return (
    <Stack spacing={1} data-test="donor-assistant-payment-cards">
      {visiblePayments.map((payment) => {
        const { date, amount } = formatPayment(payment);
        const choose = onChoose
          ? () =>
              onChoose({
                payment,
                text: t("dashboard", "donorAssistant.paymentCard.choice", {
                  amount,
                  nonprofit: payment.organizationName,
                  date,
                }),
              })
          : undefined;
        const viewDetails = onViewDetails ? () => onViewDetails(payment) : undefined;
        const activate = choose ?? viewDetails;
        const isChosen = payment.id === chosenPaymentId;
        return (
          <Stack
            key={payment.id}
            onClick={activate}
            role={activate ? "button" : undefined}
            tabIndex={activate ? 0 : undefined}
            onKeyDown={
              activate
                ? (event: KeyboardEvent<HTMLDivElement>) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      activate();
                    }
                  }
                : undefined
            }
            aria-label={t(
              "dashboard",
              choose ? "donorAssistant.paymentCard.choose" : "donorAssistant.paymentCard.open",
              { nonprofit: payment.organizationName }
            )}
            aria-pressed={isChosen || undefined}
            sx={(theme) => ({
              minWidth: 0,
              px: 1.5,
              py: 1,
              border: `1px solid ${theme.palette.border.neutral.quiet}`,
              borderRadius: theme.radius(1),
              bgcolor: theme.palette.surface.neutral.white,
              color: theme.palette.text.neutral.intense,
              cursor: activate ? "pointer" : "default",
              ...(isChosen && {
                bgcolor: theme.palette.surface.brand.intense,
                borderColor: theme.palette.border.brand.intense,
                color: theme.palette.text.reverse.intense,
              }),
              ...(activate &&
                !isChosen && {
                  "&:hover": {
                    bgcolor: theme.palette.surface.brand.quiet,
                    borderColor: theme.palette.border.brand.moderate,
                    color: theme.palette.text.brand.intense,
                  },
                }),
              "&:focus-visible": { outline: `3px solid ${theme.palette.border.brand.moderate}` },
            })}
          >
            <DashboardTypography variant="subtitle2" sx={{ color: "inherit" }}>
              {t("dashboard", "donorAssistant.paymentCard.title", {
                amount,
                form: payment.formTitle ?? payment.organizationName,
              })}
            </DashboardTypography>
            <DashboardTypography variant="body2" vibe={isChosen ? "text-reverse-quiet" : "text-neutral-quiet"}>
              {describeItems(payment, date)}
            </DashboardTypography>
          </Stack>
        );
      })}
      {actionsPayment && actions && (
        <PaymentCardActions payment={actionsPayment} control={actions.control} onOpenControl={onViewDetails} />
      )}
    </Stack>
  );
};
